import {
  Avatar as AntAvatar,
  Flex,
  Tooltip,
  Typography,
  type AvatarProps as AntdAvatarProps,
} from 'antd';
import { useMemo } from 'react';
import { Link } from 'react-router';

import useHirePortContext from '@/hooks/useHirePortContext';
import { getColorForKey } from '@/theme/avatarPalette';
import { SPACE_MD } from '@/theme/buildAntdTheme';
import { Caption } from '@/UI/typography';
import { getNameFirstLetters } from '@/utils/candidate';
import { resolvePathWithBase } from '@/utils/urlBase';

import type React from 'react';

export type AvatarColor = { main: string; light: string };

type AvatarProps = Omit<AntdAvatarProps, 'alt'> & {
  name?: string;
  color?: AvatarColor;
  imageFit?: 'cover' | 'contain';
  /** When set, wraps the avatar in an antd Tooltip with this label. */
  tooltipLabel?: string;
  /** Optional label column (generic text; callers compose domain copy). */
  title?: React.ReactNode;
  titleExtra?: React.ReactNode;
  description?: React.ReactNode;
  /**
   * Extra content stacked under the description, inside the text column — use
   * it when supporting content must align with the title rather than the
   * avatar. Switches the row to top alignment.
   */
  footer?: React.ReactNode;
  to?: string;
  href?: string;
  onClick?: () => void;
};

const AvatarImage = ({
  size,
  name,
  src,
  color,
  imageFit,
  style,
  ...rest
}: Pick<AvatarProps, 'size' | 'name' | 'src' | 'color' | 'imageFit' | 'style'> &
  Omit<
    AvatarProps,
    'title' | 'description' | 'to' | 'href' | 'tooltipLabel'
  >) => {
  const { colorScheme } = useHirePortContext();
  const resolvedColor = useMemo(
    () => color ?? getColorForKey(name ?? '', colorScheme),
    [color, name, colorScheme],
  );
  const hasSrc = Boolean(src);
  const showInitials = !hasSrc && Boolean(name);

  const rootStyle: React.CSSProperties = {
    ...style,
    ...(hasSrc && imageFit ? { objectFit: imageFit } : {}),
    ...(!hasSrc && showInitials
      ? {
          backgroundColor: resolvedColor.light,
          color: resolvedColor.main,
        }
      : {}),
  };

  return (
    <AntAvatar
      size={size}
      src={hasSrc && src}
      alt={name ?? ''}
      data-testid="ui-avatar-image"
      style={rootStyle}
      {...rest}
    >
      {showInitials ? getNameFirstLetters(name!) : null}
    </AntAvatar>
  );
};

const wrapWithTooltip = (node: React.ReactNode, tooltipLabel?: string) => {
  if (!tooltipLabel || !node) {
    return node;
  }

  return <Tooltip title={tooltipLabel}>{node}</Tooltip>;
};

type InteractiveOptions = {
  to?: string;
  href?: string;
  tooltipLabel?: string;
};

const renderInteractive = (
  node: React.ReactNode,
  { to, href, tooltipLabel }: InteractiveOptions,
) => {
  if (!node) {
    return node;
  }

  let target: React.ReactNode = node;

  if (to) {
    target = (
      <Link
        to={to}
        onClick={(event) => event.stopPropagation()}
        style={{ overflow: 'hidden' }}
      >
        {node}
      </Link>
    );
  } else if (href) {
    target = (
      <Typography.Link
        href={href}
        rel="noopener noreferrer"
        onClick={(event) => event.stopPropagation()}
        style={{ overflow: 'hidden' }}
      >
        {node}
      </Typography.Link>
    );
  } else if (tooltipLabel) {
    target = node;
  }

  return wrapWithTooltip(target, tooltipLabel);
};

const Avatar = ({
  name,
  src,
  color,
  imageFit,
  tooltipLabel,
  style,
  title,
  titleExtra,
  description,
  footer,
  to,
  href,
  className,
  size,
  onClick,
  ...rest
}: AvatarProps) => {
  const { urlBase, colorScheme } = useHirePortContext();
  const resolvedTo = to ? resolvePathWithBase(to, urlBase) : undefined;
  const displayName = name ?? (typeof title === 'string' ? title : undefined);
  const showAvatar = Boolean(src || displayName);
  const showTitle = Boolean(title);
  const showDescription =
    description != null &&
    !(typeof description === 'string' && description === '');

  const resolvedColor = useMemo(
    () => color ?? getColorForKey(displayName ?? '', colorScheme),
    [color, displayName, colorScheme],
  );

  const avatarNode = showAvatar
    ? renderInteractive(
        <AvatarImage
          size={size}
          name={displayName}
          src={src}
          color={resolvedColor}
          imageFit={imageFit}
          style={{ flexShrink: 0 }}
        />,
        { to: resolvedTo, href, tooltipLabel },
      )
    : null;

  const TitleText = size === 'large' ? Typography.Title : Typography.Text;
  const isStringDescription = typeof description === 'string';

  const textBlock = showTitle ? (
    <Flex
      vertical
      gap={footer ? SPACE_MD : 0}
      style={{ overflow: 'hidden', flex: 1 }}
    >
      <Flex vertical gap={0} style={{ overflow: 'hidden' }}>
        <Flex align="center" style={{ overflow: 'hidden' }}>
          {renderInteractive(
            <TitleText level={3} strong data-testid="ui-avatar-title">
              {title}
            </TitleText>,
            { to: resolvedTo, href, tooltipLabel },
          )}
          {titleExtra}
        </Flex>
        {showDescription && isStringDescription ? (
          <Caption
            type="secondary"
            ellipsis
            data-testid="ui-avatar-description"
          >
            {description}
          </Caption>
        ) : (
          description
        )}
      </Flex>
      {footer}
    </Flex>
  ) : null;

  const rowProps = {
    className,
    onClick,
    style: { overflow: 'hidden', ...style },
    'data-testid': 'ui-avatar',
    ...rest,
  };

  if (!showTitle && showAvatar) {
    return (
      <Flex align="center" {...rowProps}>
        {avatarNode}
      </Flex>
    );
  }

  if (showTitle && !showAvatar) {
    return (
      <Flex align="center" {...rowProps}>
        {textBlock}
      </Flex>
    );
  }

  if (showTitle && showAvatar) {
    return (
      <Flex
        gap="small"
        align={footer ? 'start' : 'center'}
        justify="start"
        {...rowProps}
      >
        {avatarNode}
        {textBlock}
      </Flex>
    );
  }

  return null;
};

Avatar.Group = AntAvatar.Group;

export default Avatar;
